import React, { useState, useEffect } from 'react';
import { FaShareAlt, FaTimes } from 'react-icons/fa';

export default function DailyQuestion({ onClose, isMobile }) {
  const [dailyQuestion, setDailyQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null); // Store the actual choice text, not index
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDailyQuestion();
  }, []);

  const fetchDailyQuestion = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/daily-question', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDailyQuestion(data.question);
      }
    } catch (error) {
      console.error('Error fetching daily question:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareQuestion = async () => {
    // Create shareable link with question ID
    const shareUrl = window.location.origin + '/practice/question/' + dailyQuestion._id;
    const questionText = dailyQuestion.sentence || dailyQuestion.question || dailyQuestion.topic || '';
    const shareText = `🎯 سؤال اليوم في منصة ياعيل ماستر!\n\n${questionText.substring(0, 100)}${questionText.length > 100 ? '...' : ''}\n\nاختبر معلوماتك!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'سؤال اليوم - ياعيل ماستر',
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        // Fallback to copy
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('تم نسخ الرابط!');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{ color: 'white', fontSize: 18 }}>جاري التحميل...</div>
      </div>
    );
  }

  if (!dailyQuestion) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: isMobile ? '1rem' : '2rem',
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      
      <div style={{
        background: 'white',
        borderRadius: 20,
        padding: isMobile ? '24px' : '32px',
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        animation: 'slideUp 0.3s ease-out',
        direction: 'rtl'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: 'rgba(0,0,0,0.05)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 20,
            color: '#666',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(0,0,0,0.05)';
          }}
        >
          <FaTimes />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
          <h2 style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: isMobile ? 22 : 28,
            fontWeight: 'bold',
            margin: 0,
            marginBottom: 8
          }}>
            سؤال اليوم
          </h2>
          <p style={{ 
            color: '#666', 
            fontSize: isMobile ? 13 : 15, 
            margin: 0,
            fontWeight: '500'
          }}>
            {new Date().toLocaleDateString('ar-EG', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Question Type Badge */}
        <div style={{ 
          textAlign: 'center',
          marginBottom: 16 
        }}>
          <span style={{
            display: 'inline-block',
            padding: '6px 16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 20,
            fontSize: isMobile ? 12 : 14,
            fontWeight: 'bold'
          }}>
            {dailyQuestion.type === 'vocab' && 'مفردات'}
            {dailyQuestion.type === 'grammar' && 'قواعد'}
            {dailyQuestion.type === 'sentence' && 'إكمال الجمل'}
            {dailyQuestion.type === 'rephrase' && 'إعادة الصياغة'}
          </span>
        </div>

        {/* Question */}
        <div style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          padding: isMobile ? '16px' : '20px',
          borderRadius: 12,
          marginBottom: 20,
          textAlign: 'right',
          border: '1px solid #e0e0e0'
        }}>
          <p style={{ 
            fontSize: isMobile ? 16 : 18, 
            lineHeight: 1.6, 
            margin: 0,
            fontWeight: '500',
            color: '#222'
          }}>
            {dailyQuestion.sentence || dailyQuestion.question || dailyQuestion.topic}
          </p>
        </div>

        {/* Choices */}
        <div style={{ marginBottom: 20 }}>
          {dailyQuestion.choices?.map((choice, index) => {
            const isCorrect = choice === dailyQuestion.correct;
            const isSelected = choice === selectedAnswer;
            
            return (
            <button
              key={index}
              onClick={() => {
                if (!showResult) {
                  setSelectedAnswer(choice);
                  setShowResult(true);
                }
              }}
              disabled={showResult}
              style={{
                width: '100%',
                padding: isMobile ? '12px 16px' : '14px 18px',
                marginBottom: 10,
                borderRadius: 12,
                border: showResult
                  ? isCorrect
                    ? '2px solid #22c55e'
                    : isSelected
                    ? '2px solid #ef4444'
                    : '1px solid #e0e0e0'
                  : isSelected
                  ? '2px solid #667eea'
                  : '1px solid #e0e0e0',
                background: showResult
                  ? isCorrect
                    ? '#dcfce7'
                    : isSelected
                    ? '#fee2e2'
                    : 'white'
                  : isSelected
                  ? '#f0f4ff'
                  : 'white',
                cursor: showResult ? 'default' : 'pointer',
                textAlign: 'right',
                fontSize: isMobile ? 14 : 16,
                transition: 'all 0.2s',
                fontWeight: isSelected ? 'bold' : 'normal',
                color: '#222'
              }}
              onMouseEnter={(e) => {
                if (!showResult && choice !== selectedAnswer) {
                  e.target.style.background = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (!showResult && choice !== selectedAnswer) {
                  e.target.style.background = 'white';
                }
              }}
            >
              {choice}
              {showResult && isCorrect && ' ✓'}
              {showResult && isSelected && !isCorrect && ' ✗'}
            </button>
            );
          })}
        </div>

        {/* Result */}
        {showResult && (
          <div style={{
            padding: isMobile ? '14px' : '16px',
            borderRadius: 12,
            background: selectedAnswer === dailyQuestion.correct
              ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' 
              : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            border: `2px solid ${selectedAnswer === dailyQuestion.correct ? '#22c55e' : '#ef4444'}`,
            marginBottom: 16,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: isMobile ? 28 : 32, marginBottom: 8 }}>
              {selectedAnswer === dailyQuestion.correct ? '🎉' : '💪'}
            </div>
            <p style={{ 
              fontSize: isMobile ? 16 : 18, 
              fontWeight: 'bold',
              color: selectedAnswer === dailyQuestion.correct ? '#166534' : '#991b1b',
              margin: 0,
              marginBottom: dailyQuestion.explanation ? 12 : 0
            }}>
              {selectedAnswer === dailyQuestion.correct
                ? 'أحسنت! إجابة صحيحة ✓' 
                : 'حاول مرة أخرى غداً!'}
            </p>
            {dailyQuestion.explanation && (
              <p style={{
                fontSize: isMobile ? 13 : 14,
                color: '#666',
                margin: 0,
                lineHeight: 1.5,
                textAlign: 'right'
              }}>
                <strong>التوضيح:</strong> {dailyQuestion.explanation}
              </p>
            )}
          </div>
        )}

        {/* Share Button */}
        {showResult && (
          <button
            onClick={shareQuestion}
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '14px',
              background: 'white',
              color: '#667eea',
              border: '2px solid #667eea',
              borderRadius: 12,
              fontSize: isMobile ? 15 : 16,
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s',
              marginBottom: 16
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#667eea';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#667eea';
            }}
          >
            <FaShareAlt />
            شارك السؤال مع أصدقائك
          </button>
        )}

        {/* Footer message */}
        <div style={{ 
          textAlign: 'center', 
          padding: isMobile ? '10px' : '12px',
          background: '#f8f9fa',
          borderRadius: 8,
          marginTop: showResult ? 0 : 16
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: isMobile ? 13 : 14, 
            color: '#666',
            lineHeight: 1.4
          }}>
            {showResult 
              ? '🔥 عدّ غداً لسؤال جديد!'
              : '💡 اختر الإجابة الصحيحة'}
          </p>
        </div>
      </div>
    </div>
  );
}

